import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { formatDate, formatDateTime } from '@/lib/utils'
import { Trash2, BarChart2, UsersRound, ClipboardList, Users, Search } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { displayTag, tagActiveClass, computeNewTags, TagIcon } from '@/components/ui/TagBadges'
import { DeleteConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAppData } from '@/contexts/AppDataContext'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'

interface ModificationExchangeProps {
    onNavigateHome: () => void
    onNavigateToEdit?: (exchangeData: any) => void
    highlightExchangeId?: string
    autoOpenBeneficiaryId?: string
    onNavigateToBeneficiaire?: (id: string) => void
    embedded?: boolean
}

interface Exchange {
    id: string
    beneficiary_id: string | null
    worker_id: string | null
    colleague_ids: string[] | null
    created_at: string | null
    updated_at: string | null
    stat_date: string
    duree: number | null
    format_echange: string | null
    contenu_echange: string | null
    contenu_echange_real: string | null
    presence: string | null
    entree_dans_appartement: string | null
    note: string | null
    submit_ts: string | null
    cvc_corps: string | null
    cvc_vetements: string | null
    cvc_comportement: string | null
    hestia_voisinage: string | null
    hestia_hygiene: string | null
    hestia_bailleur: string | null
    hestia2_securite: string | null
    hestia2_sante: string | null
    hestia2_consommation: string | null
    comment: string | null
    distribution_kit_rdr: string | null
    beneficiary_name?: string
    worker_name?: string
    colleague_names?: string[]
}

interface BeneficiaryCount {
    beneficiary_id: string
    beneficiary_name: string
    exchange_count: number
    beneficiary_tags: string[]
    is_active: boolean
}

interface BeneficiaryExchanges {
    exchanges: Exchange[]
    hasMore: boolean
    loading: boolean
    loadingMore: boolean
}

interface GroupedExchanges {
    [beneficiaryId: string]: BeneficiaryExchanges
}

const EXCHANGES_PER_PAGE = 20

export const ModificationExchange = ({ onNavigateHome, onNavigateToEdit, highlightExchangeId, autoOpenBeneficiaryId, onNavigateToBeneficiaire, embedded = false }: ModificationExchangeProps) => {
    const { summaries, loading, refreshBeneficiary, updateSummaryTags } = useAppData()
    const [search, setSearch] = useState('')
    const [showInactive, setShowInactive] = useState(false)
    const [groupedExchanges, setGroupedExchanges] = useState<GroupedExchanges>({})
    const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({})
    const [deletingExchangeId, setDeletingExchangeId] = useState<string | null>(null)
    const [highlightedExchangeId, setHighlightedExchangeId] = useState(highlightExchangeId)
    const exchangeRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

    const allBeneficiaryCounts: BeneficiaryCount[] = summaries
        .filter(s => (showInactive || s.is_active) && (s.prenom || s.nom || s.name) && s.stat_count > 0)
        .sort((a, b) => (a.numero_suivi ?? Infinity) - (b.numero_suivi ?? Infinity))
        .map(s => ({
            beneficiary_id: s.id,
            beneficiary_name: displayBeneficiaryName(s),
            exchange_count: s.stat_count,
            beneficiary_tags: s.tags ?? [],
            is_active: s.is_active,
        }))

    const beneficiaryCounts = allBeneficiaryCounts.filter(b =>
        !search || b.beneficiary_name.toLowerCase().includes(search.toLowerCase())
    )

    // Auto-open beneficiary section once beneficiary list is loaded
    useEffect(() => {
        if (!autoOpenBeneficiaryId || !beneficiaryCounts.length) return
        setOpenSections(prev => ({ ...prev, [autoOpenBeneficiaryId]: true }))
        fetchExchangesForBeneficiary(autoOpenBeneficiaryId, 0, highlightExchangeId ?? undefined)
    }, [autoOpenBeneficiaryId, beneficiaryCounts.length])

    useEffect(() => {
        if (!highlightedExchangeId) return
        const el = exchangeRefs.current[highlightedExchangeId]
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const timer = setTimeout(() => setHighlightedExchangeId(undefined), 3000)
            return () => clearTimeout(timer)
        }
    }, [groupedExchanges, highlightedExchangeId])

    const fetchExchangesForBeneficiary = async (beneficiaryId: string, offset: number = 0, targetExchangeId?: string) => {
        try {
            // Set loading state
            setGroupedExchanges(prev => ({
                ...prev,
                [beneficiaryId]: {
                    ...prev[beneficiaryId],
                    loading: offset === 0,
                    loadingMore: offset > 0
                }
            }))

            // Fetch exchanges for specific beneficiary with pagination from stats table
            const { data: exchangesData, error: exchangesError } = await supabase
                .from('stats')
                .select(`
                    *,
                    beneficiaries(name, prenom, nom, numero_suivi),
                    workers(name)
                `)
                .eq('beneficiary_id', beneficiaryId)
                .is('deleted_dt', null)
                .order('stat_date', { ascending: false })
                .range(offset, offset + EXCHANGES_PER_PAGE - 1)

            if (exchangesError) {
                throw exchangesError
            }

            // Get all unique colleague IDs from this batch of exchanges
            const allColleagueIds = new Set<string>()
            exchangesData.forEach(exchange => {
                if (exchange.colleague_ids && exchange.colleague_ids.length > 0) {
                    exchange.colleague_ids.forEach((id: string) => allColleagueIds.add(id))
                }
            })

            // Fetch all colleague names in one query (both active and inactive for historical data)
            let colleaguesMap: { [id: string]: string } = {}
            if (allColleagueIds.size > 0) {
                const { data: colleaguesData, error: colleaguesError } = await supabase
                    .from('workers')
                    .select('id, name')
                    .in('id', Array.from(allColleagueIds))

                if (!colleaguesError && colleaguesData) {
                    colleaguesMap = colleaguesData.reduce((acc: { [id: string]: string }, colleague: any) => {
                        if (colleague.id && colleague.name) {
                            acc[colleague.id] = colleague.name
                        }
                        return acc
                    }, {} as { [id: string]: string })
                }
            }

            // Process the exchanges with colleague names
            const processedExchanges: Exchange[] = exchangesData.map((exchange) => {
                let colleagueNames: string[] = []

                if (exchange.colleague_ids && exchange.colleague_ids.length > 0) {
                    colleagueNames = exchange.colleague_ids
                        .map((id: string) => colleaguesMap[id])
                        .filter((name: string) => name)
                }

                return {
                    ...exchange,
                    beneficiary_name: displayBeneficiaryName((exchange.beneficiaries as any) ?? {}) || 'Inconnu',
                    worker_name: (exchange.workers as any)?.name || 'Inconnu',
                    colleague_names: colleagueNames
                }
            })

            // Update the grouped exchanges
            setGroupedExchanges(prev => {
                const existing = prev[beneficiaryId]?.exchanges || []
                const newExchanges = offset === 0 ? processedExchanges : [...existing, ...processedExchanges]

                return {
                    ...prev,
                    [beneficiaryId]: {
                        exchanges: newExchanges,
                        hasMore: exchangesData.length === EXCHANGES_PER_PAGE,
                        loading: false,
                        loadingMore: false
                    }
                }
            })

            // If highlight target not yet loaded, keep fetching
            if (targetExchangeId && !processedExchanges.find(e => e.id === targetExchangeId) && exchangesData.length === EXCHANGES_PER_PAGE) {
                await fetchExchangesForBeneficiary(beneficiaryId, offset + EXCHANGES_PER_PAGE, targetExchangeId)
            }

        } catch (error) {
            console.error('Error fetching exchanges for beneficiary:', error)
            setGroupedExchanges(prev => ({
                ...prev,
                [beneficiaryId]: {
                    ...prev[beneficiaryId],
                    loading: false,
                    loadingMore: false
                }
            }))
        }
    }

    const toggleSection = async (beneficiaryId: string) => {
        const isCurrentlyOpen = openSections[beneficiaryId]

        setOpenSections(prev => ({
            ...prev,
            [beneficiaryId]: !isCurrentlyOpen
        }))

        // If opening and no exchanges loaded yet, fetch them
        if (!isCurrentlyOpen && !groupedExchanges[beneficiaryId]?.exchanges?.length) {
            await fetchExchangesForBeneficiary(beneficiaryId)
        }
    }

    const loadMoreExchanges = async (beneficiaryId: string) => {
        const currentExchanges = groupedExchanges[beneficiaryId]?.exchanges || []
        await fetchExchangesForBeneficiary(beneficiaryId, currentExchanges.length)
    }

    const handleEditExchange = async (exchange: Exchange) => {
        try {
            console.log('handleEditExchange called with exchange:', {
                id: exchange.id,
                beneficiary_name: exchange.beneficiary_name,
                worker_name: exchange.worker_name,
                colleague_ids: exchange.colleague_ids
            })

            // Convert the exchange data back to the form format expected by the exchange steps
            const formData = {
                beneficiary_id: exchange.beneficiary_id,
                worker_id: exchange.worker_id,
                colleague_ids: exchange.colleague_ids,
                info: {
                    uuid: exchange.id,
                    locataire: exchange.beneficiary_name,
                    travailleur: exchange.worker_name,
                    collegue: exchange.colleague_names || [],
                    date: exchange.stat_date,
                    duree: exchange.duree,
                    format_echange: exchange.format_echange,
                    contenu_echange: exchange.contenu_echange,
                    contenu_echange_real: exchange.contenu_echange_real?.split(', ') || [],
                    presence: exchange.presence,
                    entree_dans_appartement: exchange.entree_dans_appartement,
                    note: exchange.note,
                    submit_ts: exchange.submit_ts
                },
                cvc: exchange.cvc_corps ? {
                    corps: exchange.cvc_corps,
                    vetements: exchange.cvc_vetements,
                    comportement: exchange.cvc_comportement
                } : null,
                hestia: exchange.hestia_voisinage ? {
                    voisinage: exchange.hestia_voisinage,
                    hygiene: exchange.hestia_hygiene,
                    bailleur: exchange.hestia_bailleur
                } : null,
                hestia2: exchange.hestia2_securite ? {
                    securite: exchange.hestia2_securite,
                    sante: exchange.hestia2_sante,
                    consommation: exchange.hestia2_consommation
                } : null
            }

            if (onNavigateToEdit) {
                onNavigateToEdit(formData)
            }
        } catch (error) {
            console.error('Error preparing exchange for edit:', error)
        }
    }

    const handleDeleteExchange = async (exchangeId: string, beneficiaryId: string) => {
        const { error } = await supabase
            .from('stats')
            .update({ deleted_dt: new Date().toISOString() })
            .eq('id', exchangeId)
        if (error) {
            console.error('Error deleting exchange:', error)
            return
        }
        setGroupedExchanges(prev => ({
            ...prev,
            [beneficiaryId]: {
                ...prev[beneficiaryId],
                exchanges: prev[beneficiaryId].exchanges.filter(e => e.id !== exchangeId)
            }
        }))
        refreshBeneficiary(beneficiaryId)
        setDeletingExchangeId(null)
    }

    const handleToggleTag = async (beneficiaryId: string, tag: string) => {
        const current = summaries.find(s => s.id === beneficiaryId)?.tags ?? []
        const newTags = computeNewTags(current, tag)
        updateSummaryTags(beneficiaryId, newTags)
        await supabase.from('beneficiaries').update({ tags: newTags }).eq('id', beneficiaryId)
    }

    const keyTextMap: { [key: string]: string } = {
        // Stats table field names (no double underscores)
        stat_date: "Date de l'échange",
        duree: "Durée (min)",
        format_echange: "Format de l'échange",
        contenu_echange: "Contenu initialement prévu",
        contenu_echange_real: "Contenu effectivement abordé",
        presence: "Présence physique",
        entree_dans_appartement: "Entrée dans l'appartement",
        note: "Commentaire",
        submit_ts: "Envoyé le",
        comment: "Commentaire additionnel",
        distribution_kit_rdr: "Distribution kit RDR",

        // CVC fields
        cvc_corps: "CVC - Corps",
        cvc_vetements: "CVC - Vêtements",
        cvc_comportement: "CVC - Comportement",

        // Hestia fields
        hestia_voisinage: "Hestia - Voisinage",
        hestia_hygiene: "Hestia - Hygiène",
        hestia_bailleur: "Hestia - Bailleur",

        // Hestia 2 fields
        hestia2_securite: "Hestia 2 - Sécurité",
        hestia2_sante: "Hestia 2 - Santé",
        hestia2_consommation: "Hestia 2 - Consommation",

        // System fields
        updated_at: "Modifié le",

        // Processed names
        colleague_names: "Collègue.s HF présent.e.s",
    }

    if (loading) {
        return (
            <div className={embedded ? 'py-12 flex justify-center' : 'min-h-screen bg-gray-50 pt-24 flex justify-center'}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des données...</p>
                </div>
            </div>
        )
    }

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
            {!embedded && <PageHeader title="Consultation de stats" icon={<BarChart2 size={20} />} onBack={onNavigateHome} />}

            <main className="max-w-4xl mx-auto py-8 px-4">
                {beneficiaryCounts.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center">
                        <p className="text-gray-600 mb-6">Aucun échange trouvé.</p>
                        <Button onClick={onNavigateHome}>
                            <ChevronLeft className="h-4 w-4 mr-2" />
                            Retour
                        </Button>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 space-y-3">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Users className="mr-2" size={20} />
                                Locataires ({beneficiaryCounts.length}{search ? ` / ${allBeneficiaryCounts.length}` : ''})
                            </h2>
                            <div className="flex gap-3 items-center">
                                <div className="relative flex-1">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        placeholder="Rechercher par nom, prénom..."
                                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={showInactive}
                                        onChange={e => setShowInactive(e.target.checked)}
                                        className="rounded"
                                    />
                                    Inactifs
                                </label>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                        {beneficiaryCounts.map((beneficiary) => {
                            const isOpen = openSections[beneficiary.beneficiary_id]
                            const beneficiaryData = groupedExchanges[beneficiary.beneficiary_id]
                            const exchanges = beneficiaryData?.exchanges || []

                            return (
                                <div key={beneficiary.beneficiary_id}>
                                    <div
                                        className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => toggleSection(beneficiary.beneficiary_id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                                                    <Users size={20} className="text-blue-600" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className={`text-lg font-medium ${beneficiary.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                                                            {beneficiary.beneficiary_name}
                                                            {!beneficiary.is_active && <span className="ml-2 text-xs font-normal text-gray-400 italic">(inactif)</span>}
                                                        </h3>
                                                        {onNavigateToBeneficiaire && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); onNavigateToBeneficiaire(beneficiary.beneficiary_id) }}
                                                                title="Voir la fiche"
                                                                className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                                            >
                                                                <ClipboardList size={15} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-500">
                                                        {beneficiary.exchange_count} stat{beneficiary.exchange_count !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                    {beneficiary.exchange_count}
                                                </span>
                                                <div className="ml-4 text-gray-400">
                                                    {isOpen ? '↓' : '→'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                        {isOpen && (
                                            <div className="mt-4">
                                                {beneficiaryData?.loading ? (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-600">Chargement des échanges...</p>
                                                    </div>
                                                ) : exchanges.length === 0 ? (
                                                    <div className="text-center py-4">
                                                        <p className="text-gray-600">Aucun échange trouvé pour ce locataire.</p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {exchanges.map((exchange) => (
                                                            <div key={exchange.id} ref={el => { exchangeRefs.current[exchange.id] = el }} className={`border rounded-lg p-4 bg-gray-50 transition-all duration-700 ${exchange.id === highlightedExchangeId ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}>
                                                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                                    <div className="lg:col-span-1">
                                                                        <h4 className="font-semibold text-gray-900 mb-2">
                                                                            {beneficiary.beneficiary_name} avec {exchange.worker_name}
                                                                        </h4>
                                                                        <p className="text-sm text-gray-600 mb-1">
                                                                            <span className="font-medium">Date:</span> {formatDate(exchange.stat_date)}
                                                                        </p>
                                                                        {exchange.submit_ts && (
                                                                            <p className="text-sm text-gray-600 mb-1">
                                                                                <span className="font-medium">Envoyé:</span> {formatDateTime(exchange.submit_ts)}
                                                                            </p>
                                                                        )}
                                                                        {exchange.updated_at && exchange.updated_at.substring(0, 19) !== exchange.submit_ts?.substring(0, 19) && (
                                                                            <p className="text-sm text-gray-600 mb-3">
                                                                                <span className="font-medium">Modifié:</span> {formatDateTime(exchange.updated_at)}
                                                                            </p>
                                                                        )}
                                                                        {!IS_DEMO_MODE && (<>
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleEditExchange(exchange)}
                                                                            className="w-full"
                                                                        >
                                                                            Modifier
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="w-full mt-2 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                                            onClick={() => setDeletingExchangeId(exchange.id)}
                                                                            title="Supprimer"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </Button>
                                                                        </>)}
                                                                    </div>

                                                                    <div className="lg:col-span-2">
                                                                        <div className="space-y-2 text-sm">
                                                                            {/* Show colleague names */}
                                                                            {exchange.colleague_names && exchange.colleague_names.length > 0 && (
                                                                                <div className="flex">
                                                                                    <span className="font-medium text-gray-700 min-w-[120px]">
                                                                                        {keyTextMap.colleague_names}:
                                                                                    </span>
                                                                                    <span className="text-gray-600 ml-2">
                                                                                        {exchange.colleague_names.join(', ')}
                                                                                    </span>
                                                                                </div>
                                                                            )}

                                                                            {Object.entries(exchange).map(([key, value]) => {
                                                                                // Skip fields that shouldn't be displayed
                                                                                if (
                                                                                    key === 'beneficiaries' ||
                                                                                    key === 'workers' ||
                                                                                    key === 'created_by' ||
                                                                                    key === 'created_at' ||
                                                                                    key === 'updated_at' ||
                                                                                    key.startsWith('postprocess')
                                                                                ) {
                                                                                    return null
                                                                                }

                                                                                // Skip if no value (but allow 0 and false)
                                                                                if (value === null || value === undefined || value === '') {
                                                                                    return null
                                                                                }

                                                                                // Skip date and submit_ts as they're handled specially above
                                                                                if (key === 'submit_ts' || key === 'stat_date') {
                                                                                    return null
                                                                                }

                                                                                // Only display fields that are in keyTextMap
                                                                                if (!keyTextMap[key]) {
                                                                                    return null
                                                                                }

                                                                                const displayKey = keyTextMap[key]
                                                                                let displayValue = value

                                                                                // Format arrays
                                                                                if (Array.isArray(value)) {
                                                                                    displayValue = value.join(', ')
                                                                                }

                                                                                // Format comma-separated strings
                                                                                if (typeof value === 'string' && value.includes(',')) {
                                                                                    displayValue = value.split(',').join(', ')
                                                                                }

                                                                                return (
                                                                                    <div key={key} className="flex">
                                                                                        <span className="font-medium text-gray-700 min-w-[150px]">
                                                                                            {displayKey}:
                                                                                        </span>
                                                                                        <span className="text-gray-600 ml-2">
                                                                                            {displayValue}
                                                                                        </span>
                                                                                    </div>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {/* Load more button */}
                                                        {beneficiaryData?.hasMore && (
                                                            <div className="text-center pt-4">
                                                                <Button
                                                                    variant="outline"
                                                                    onClick={() => loadMoreExchanges(beneficiary.beneficiary_id)}
                                                                    disabled={beneficiaryData?.loadingMore}
                                                                >
                                                                    {beneficiaryData?.loadingMore ? 'Chargement...' : 'Charger plus anciens'}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>
                            )
                        })}
                        </div>
                    </div>
                )}
            </main>
            {deletingExchangeId && (
                <DeleteConfirmDialog
                    onConfirm={() => {
                        const beneficiaryId = Object.keys(groupedExchanges).find(bId =>
                            groupedExchanges[bId].exchanges.some(e => e.id === deletingExchangeId)
                        )
                        if (beneficiaryId) handleDeleteExchange(deletingExchangeId, beneficiaryId)
                    }}
                    onCancel={() => setDeletingExchangeId(null)}
                />
            )}
        </div>
    )
}